/**
 * Notification helpers — Laravel Notifications API v1
 */

/** Poll interval for unread badge (ms) */
export const NOTIFICATION_POLL_MS = 30_000;

/**
 * Skip opportunistic unread-count fetches (route/focus/visibility)
 * if a count was fetched more recently than this.
 */
export const NOTIFICATION_COUNT_MIN_INTERVAL_MS = 15_000;

/** Defer the first badge fetch so page APIs can use HTTP connections first */
export const NOTIFICATION_INITIAL_IDLE_MS = 800;

/** Dropdown list considered fresh for this long (ms) */
export const NOTIFICATION_DROPDOWN_STALE_MS = 60_000;

/** Debounce hover / count-triggered prefetch (ms) */
export const NOTIFICATION_PREFETCH_DEBOUNCE_MS = 300;

/**
 * Icon name per category (mdi class suffix)
 * @param {string} category
 * @returns {string}
 */
export function categoryIcon(category) {
  const icons = {
    ticket: "mdi-ticket-outline",
    negotiation: "mdi-handshake-outline",
    consultation: "mdi-briefcase-outline",
  };
  return icons[category] || "mdi-bell-outline";
}

/**
 * @param {{ read_at?: string|null }} notification
 * @returns {boolean}
 */
export function isUnread(notification) {
  return notification.read_at == null;
}

/**
 * @param {{ action?: object|null }} notification
 * @param {Record<string, (action: object, n: object) => string|null>} routeMap
 * @returns {string|null}
 */
export function resolveNotificationRoute(notification, routeMap = {}) {
  const action = notification.action;
  if (!action) return null;

  if (routeMap[action.type]) {
    return routeMap[action.type](action, notification);
  }

  if (action.type === "ticket" && action.ticket_number) {
    return `/tickets/${action.ticket_number}`;
  }

  if (action.type === "consultation" && action.consultation_id) {
    const id = action.consultation_id;
    switch (action.screen) {
      case "chat":
        return `/consultations/${id}/chat`;
      case "offer":
        return `/consultations/${id}/offer`;
      case "invoice":
        return `/consultations/${id}/invoice`;
      case "payment":
        return `/consultations/${id}/payment`;
      case "response":
        return `/consultations/${id}/response`;
      default:
        return `/consultations/${id}`;
    }
  }

  return null;
}

/**
 * @param {string} isoDate
 * @param {'en' | 'ar'} locale
 * @returns {string}
 */
export function formatNotificationTime(isoDate, locale = "en") {
  const date = new Date(isoDate);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) {
    return locale === "ar" ? "الآن" : "Just now";
  }
  if (minutes < 60) {
    return locale === "ar" ? `منذ ${minutes} د` : `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return locale === "ar" ? `منذ ${hours} س` : `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return locale === "ar" ? `منذ ${days} ي` : `${days}d ago`;
}
